/* copy-register: app */
import { useId, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

export interface ContextRecordPreviewLabels {
  showDetails: string;
  hideDetails: string;
}

export interface ContextRecordPreviewMetadata {
  label: string;
  value: ReactNode;
}

export interface ContextRecordPreviewProps {
  id: string;
  variant: "role" | "job";
  heading: string;
  summary: string;
  labels: ContextRecordPreviewLabels;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  canonicalLinkLabel: string;
  to?: string;
  metadata?: readonly ContextRecordPreviewMetadata[];
  detail?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  unavailableReason?: string;
}

/**
 * A bounded contextual record row. The parent owns disclosure state so a
 * named group can enforce the one-open-sibling rule without hidden coupling.
 */
export function ContextRecordPreview({
  id,
  variant,
  heading,
  summary,
  labels,
  expanded,
  onExpandedChange,
  canonicalLinkLabel,
  to,
  metadata = [],
  detail,
  headingLevel = 3,
  unavailableReason
}: ContextRecordPreviewProps) {
  const generatedId = useId();
  const detailId = `${id || generatedId}-context-record-detail`;
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4";
  const available = Boolean(to);
  const canDisclose = Boolean(detail) && !unavailableReason;

  return (
    <article
      className={`apf-context-record-preview apf-context-record-preview--${variant}`}
      data-unavailable={unavailableReason ? "true" : undefined}
    >
      <div className="apf-context-record-preview__body">
        <Heading className="apf-context-record-preview__heading">
          {available ? <Link to={to!}>{heading}</Link> : heading}
        </Heading>
        <p className="apf-context-record-preview__summary">{summary}</p>
        {metadata.length ? (
          <dl className="apf-context-record-preview__metadata">
            {metadata.map((item, index) => (
              <div key={`${item.label}-${index}`}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {unavailableReason ? (
          <p className="apf-context-record-preview__unavailable" role="status">{unavailableReason}</p>
        ) : null}
        {canDisclose ? (
          <div className="apf-context-record-preview__detail" id={detailId} hidden={!expanded}>
            {detail}
          </div>
        ) : null}
      </div>
      <div className="apf-context-record-preview__actions">
        {canDisclose ? (
          <button
            className="apf-context-record-preview__disclosure"
            type="button"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={() => onExpandedChange(!expanded)}
          >
            {expanded ? labels.hideDetails : labels.showDetails}
            <span aria-hidden="true">{expanded ? "−" : "+"}</span>
          </button>
        ) : null}
        {available ? <Link className="apf-context-record-preview__canonical-link" to={to!}>{canonicalLinkLabel}</Link> : null}
      </div>
    </article>
  );
}

export interface CatalogueFacetOption {
  id: string;
  label: string;
  context?: string;
  count?: number;
}

export interface CatalogueFacetLabels {
  search: (label: string) => string;
  clear: (label: string) => string;
  clearAll: string;
  selectedCount: (count: number) => string;
  moreCount: (count: number) => string;
  noOptions: string;
  tryAgain: string;
  resultCount: (count: number) => string;
  optionCount: (count: number) => string;
  unavailableCount: (count: number) => string;
  clearUnavailable: string;
}

export type CatalogueFacetStatus = "ready" | "loading" | "partial" | "error" | "permission-degraded";

export interface CatalogueFacetProps {
  id: string;
  label: string;
  labels: CatalogueFacetLabels;
  options: readonly CatalogueFacetOption[];
  selectedIds: readonly string[];
  onSelectedIdsChange: (selectedIds: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status?: CatalogueFacetStatus;
  statusMessage?: string;
  onRetry?: () => void;
  unavailableSelectedCount?: number;
  onClearUnavailable?: () => void;
  onClearAll?: () => void;
}

function normaliseSearch(value: string): string {
  return value.normalize("NFKC").trim().toLocaleLowerCase();
}

function matchesSearch(value: string, query: string): boolean {
  const terms = normaliseSearch(query).split(/\s+/u).filter(Boolean);
  if (!terms.length) return true;
  const candidate = normaliseSearch(value);
  return terms.every((term) => candidate.includes(term));
}

/** A controlled, URL-ready multi-select facet built from native controls. */
export function CatalogueFacet({
  id,
  label,
  labels,
  options,
  selectedIds,
  onSelectedIdsChange,
  open,
  onOpenChange,
  status = "ready",
  statusMessage,
  onRetry,
  unavailableSelectedCount = 0,
  onClearUnavailable,
  onClearAll
}: CatalogueFacetProps) {
  const [query, setQuery] = useState("");
  const panelId = `${id}-facet-panel`;
  const searchId = `${id}-facet-search`;
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const optionById = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const selectedOptions = selectedIds.flatMap((selectedId) => {
    const option = optionById.get(selectedId);
    return option ? [option] : [];
  });
  const visibleOptions = useMemo(() => {
    return options.filter((option) => matchesSearch([option.label, option.context].filter(Boolean).join(" "), query));
  }, [options, query]);
  const selectedSummary = selectedOptions.slice(0, 2).map(({ label: optionLabel }) => optionLabel);
  const moreCount = Math.max(0, selectedOptions.length - selectedSummary.length);
  const selectedCount = selectedOptions.length + unavailableSelectedCount;
  const isLoading = status === "loading";
  const isError = status === "error";

  function toggle(selectedId: string, checked: boolean): void {
    const next = checked
      ? [...selectedIds.filter((candidate) => candidate !== selectedId), selectedId]
      : selectedIds.filter((candidate) => candidate !== selectedId);
    onSelectedIdsChange(next);
  }

  return (
    <section className="apf-catalogue-facet" data-status={status} aria-labelledby={`${id}-facet-label`}>
      <div className="apf-catalogue-facet__header">
        <div>
          <h3 id={`${id}-facet-label`}>{label}</h3>
          {selectedCount ? (
            <p className="apf-catalogue-facet__summary">
              <span>{labels.selectedCount(selectedCount)}</span>
              {selectedSummary.map((selectedLabel, index) => <span key={`${selectedLabel}-${index}`}>{selectedLabel}</span>)}
              {moreCount ? <span>{labels.moreCount(moreCount)}</span> : null}
              {unavailableSelectedCount ? <span>{labels.unavailableCount(unavailableSelectedCount)}</span> : null}
            </p>
          ) : null}
        </div>
        <button
          className="apf-catalogue-facet__toggle"
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-labelledby={`${id}-facet-label ${id}-facet-toggle-count`}
          onClick={() => onOpenChange(!open)}
        >
          <span id={`${id}-facet-toggle-count`}>{labels.selectedCount(selectedCount)}</span>
          <span aria-hidden="true">{open ? "−" : "+"}</span>
        </button>
      </div>
      {open ? (
        <div className="apf-catalogue-facet__panel" id={panelId}>
          {selectedOptions.length ? (
            <ul className="apf-catalogue-facet__selected" aria-label={labels.selectedCount(selectedOptions.length)}>
              {selectedOptions.map((option) => (
                <li key={option.id}>
                  <span>{option.label}</span>
                  <button type="button" onClick={() => toggle(option.id, false)}>{labels.clear(option.label)}</button>
                </li>
              ))}
            </ul>
          ) : null}
          {unavailableSelectedCount ? (
            <div className="apf-catalogue-facet__unavailable" role="status">
              <p>{labels.unavailableCount(unavailableSelectedCount)}</p>
              {onClearUnavailable ? <button type="button" onClick={onClearUnavailable}>{labels.clearUnavailable}</button> : null}
            </div>
          ) : null}
          <label className="apf-catalogue-facet__search" htmlFor={searchId}>
            <span>{labels.search(label)}</span>
            <input
              id={searchId}
              type="search"
              value={query}
              disabled={isLoading}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <p className="apf-visually-hidden" role="status" aria-live="polite">{labels.resultCount(visibleOptions.length)}</p>
          {statusMessage ? <p className="apf-catalogue-facet__status" role="status">{statusMessage}</p> : null}
          {isError ? (
            onRetry ? <button className="apf-catalogue-facet__retry" type="button" onClick={onRetry}>{labels.tryAgain}</button> : null
          ) : visibleOptions.length ? (
            <fieldset className="apf-catalogue-facet__options" disabled={isLoading}>
              <legend className="apf-visually-hidden">{label}</legend>
              {visibleOptions.map((option) => (
                <label className="apf-catalogue-facet__option" key={option.id}>
                  <input
                    type="checkbox"
                    checked={selectedSet.has(option.id)}
                    onChange={(event) => toggle(option.id, event.target.checked)}
                  />
                  <span className="apf-catalogue-facet__option-label">
                    <strong>{option.label}</strong>
                    {option.context ? <small>{option.context}</small> : null}
                  </span>
                  {typeof option.count === "number" ? <span className="apf-catalogue-facet__option-count">{labels.optionCount(option.count)}</span> : null}
                </label>
              ))}
            </fieldset>
          ) : <p className="apf-catalogue-facet__empty">{labels.noOptions}</p>}
          <div className="apf-catalogue-facet__actions">
            <button type="button" disabled={!selectedIds.length} onClick={() => onSelectedIdsChange([])}>{labels.clear(label)}</button>
            {onClearAll ? <button type="button" onClick={onClearAll}>{labels.clearAll}</button> : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export type SupervisionModeId =
  | "supervise-work"
  | "explore-evidence"
  | "review-learning"
  | "open-office-artifact"
  | (string & {});

export interface SupervisionOfficeSequenceHandoff {
  steps: readonly SupervisionOfficeHandoffStep[];
  returnLabel: string;
  returnRecord: ReactNode;
  returnState: SupervisionOutcomeState;
  returnStateLabel: string;
}

/** Compatibility shape for callers that have not yet adopted an ordered Office sequence. */
export interface SupervisionOfficeLinkHandoff {
  label: string;
  to: string;
  description: string;
}

export type SupervisionOfficeHandoff = SupervisionOfficeSequenceHandoff | SupervisionOfficeLinkHandoff;

export type SupervisionOutcomeState =
  | "proposal"
  | "awaiting-review"
  | "committing"
  | "committed"
  | "blocked-by-authority"
  | "failed"
  | "reversed";

export interface SupervisionRecordView {
  id: string;
  label: ReactNode;
  detail: ReactNode;
  state: string;
  stateLabel: string;
  outcomeState: SupervisionOutcomeState;
  owner: ReactNode;
  evidenceRecordIds: readonly string[];
}

export interface SupervisionOfficeHandoffStep {
  product: "composer" | "conductor";
  productLabel: string;
  label: ReactNode;
  artifactType: ReactNode;
  reviewedVersion: ReactNode;
  scope: ReactNode;
  proposal: ReactNode;
  readBackResult: ReactNode;
  state: string;
  stateLabel: string;
  to: string;
  linkLabel: string;
}

export interface SupervisionEvidenceView {
  id: string;
  label: ReactNode;
  definition: ReactNode;
  owner: ReactNode;
  source: ReactNode;
  provenance: ReactNode;
  freshness: ReactNode;
  observedAt: ReactNode;
}

export interface SupervisionAuthorityView {
  summary: ReactNode;
  source: ReactNode;
  posture: ReactNode;
  grantor: ReactNode;
  grantRequirement?: ReactNode;
  allowedActionClasses: readonly ReactNode[];
  interventionRights: ReactNode;
  humanSupervision: readonly ReactNode[];
  escalationTriggers: readonly ReactNode[];
  escalationOwner: ReactNode;
  recovery: readonly ReactNode[];
}

export interface SupervisionLearningView {
  evaluation: ReactNode;
  proposedAdaptation: ReactNode;
  releaseOwner: ReactNode;
  rollback: readonly ReactNode[];
}

export interface SupervisionModeView {
  id: SupervisionModeId;
  label: string;
  question: string;
  primary: ReactNode;
  evidence: ReactNode;
  evidenceRecords?: readonly SupervisionEvidenceView[];
  authority: SupervisionAuthorityView;
  outcomeState: SupervisionOutcomeState;
  outcomeStateLabel: string;
  outcome: ReactNode;
  records?: readonly SupervisionRecordView[];
  learning?: SupervisionLearningView;
  officeHandoff?: SupervisionOfficeHandoff;
}

export interface ModuleAgentSupervisionFrameLabels {
  illustrative: string;
  synthetic: string;
  modeLabel: string;
  roleSituation: string;
  agentMission: string;
  evidence: string;
  authority: string;
  outcomeOwner: string;
  outcome: string;
  learning: string;
  officeHandoff: string;
  moduleStatus: string;
  proofMaturity: string;
  evidenceOwner: string;
  evidenceSource: string;
  provenance: string;
  freshness: string;
  observedAt: string;
  recordOwner: string;
  authoritySource: string;
  authorityPosture: string;
  grantor: string;
  actionScope: string;
  interventionRights: string;
  humanSupervision: string;
  escalation: string;
  escalationOwner: string;
  recovery: string;
  learningEvaluation: string;
  learningProposal: string;
  releaseOwner: string;
  rollback: string;
  artifact: string;
  reviewedVersion: string;
  scope: string;
  proposedMutation: string;
  readBackResult: string;
  workblockReturn: string;
}

export interface ModuleAgentSupervisionFrameProps {
  labels: ModuleAgentSupervisionFrameLabels;
  role: { label: string; situation: string; outcomeOwner: string };
  agent: { label: string; mission: string };
  moduleStatus: string;
  proofMaturity: string;
  mode: SupervisionModeId;
  modes: readonly SupervisionModeView[];
  onModeChange: (mode: SupervisionModeId) => void;
  fixtureState?: "ready" | "loading" | "partial" | "stale" | "blocked" | "failed" | "reversed";
  fixtureStateLabel?: string;
  proofClaim?: string;
}

/**
 * A read-only operating-model illustration. The only controls change local
 * presentation; Office destinations are links and no action can commit work.
 */
export function ModuleAgentSupervisionFrame({
  labels,
  role,
  agent,
  moduleStatus,
  proofMaturity,
  mode,
  modes,
  onModeChange,
  fixtureState = "ready",
  fixtureStateLabel,
  proofClaim
}: ModuleAgentSupervisionFrameProps) {
  const headingId = useId();
  const evidenceIdPrefix = useId().replace(/:/gu, "");
  const selectedMode = modes.find((candidate) => candidate.id === mode) ?? modes[0];
  if (!selectedMode) return null;
  const evidenceById = new Map((selectedMode.evidenceRecords ?? []).map((record) => [record.id, record]));
  const evidenceAnchor = (id: string) => `${evidenceIdPrefix}-${id.replace(/[^a-z0-9_-]/giu, "-")}`;

  return (
    <section className="apf-module-agent-supervision-frame" data-fixture-state={fixtureState} aria-labelledby={headingId}>
      <div className="apf-module-agent-supervision-frame__header">
        <p className="apf-module-agent-supervision-frame__proof-line">
          <span>{labels.illustrative}</span>
          <span>{labels.synthetic}</span>
          {fixtureStateLabel ? <span>{fixtureStateLabel}</span> : null}
        </p>
        <div className="apf-module-agent-supervision-frame__identity">
          <div>
            <p>{labels.roleSituation}</p>
            <h2 id={headingId}>{role.label}</h2>
            <p>{role.situation}</p>
          </div>
          <div>
            <p>{labels.agentMission}</p>
            <h3>{agent.label}</h3>
            <p>{agent.mission}</p>
          </div>
        </div>
        <dl className="apf-module-agent-supervision-frame__maturity">
          <div><dt>{labels.moduleStatus}</dt><dd>{moduleStatus}</dd></div>
          <div><dt>{labels.proofMaturity}</dt><dd>{proofMaturity}</dd></div>
        </dl>
      </div>
      <label className="apf-module-agent-supervision-frame__mode-select">
        <span>{labels.modeLabel}</span>
        <select value={selectedMode.id} onChange={(event) => onModeChange(event.target.value as SupervisionModeId)}>
          {modes.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}
        </select>
      </label>
      <div className="apf-module-agent-supervision-frame__mode" aria-live="polite">
        <div className="apf-module-agent-supervision-frame__mode-head">
          <p>{selectedMode.label}</p>
          <h3>{selectedMode.question}</h3>
          <div>{selectedMode.primary}</div>
        </div>
        <div className="apf-module-agent-supervision-frame__regions">
          <section>
            <h4>{labels.evidence}</h4>
            <div>{selectedMode.evidence}</div>
            {selectedMode.evidenceRecords?.length ? (
              <ul className="apf-module-agent-supervision-frame__evidence-records">
                {selectedMode.evidenceRecords.map((record) => (
                  <li id={evidenceAnchor(record.id)} key={record.id}>
                    <strong>{record.label}</strong>
                    <p>{record.definition}</p>
                    <dl>
                      <div><dt>{labels.evidenceOwner}</dt><dd>{record.owner}</dd></div>
                      <div><dt>{labels.evidenceSource}</dt><dd>{record.source}</dd></div>
                      <div><dt>{labels.provenance}</dt><dd>{record.provenance}</dd></div>
                      <div><dt>{labels.freshness}</dt><dd>{record.freshness}</dd></div>
                      <div><dt>{labels.observedAt}</dt><dd>{record.observedAt}</dd></div>
                    </dl>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
          <section>
            <h4>{labels.authority}</h4>
            <div>{selectedMode.authority.summary}</div>
            <dl className="apf-module-agent-supervision-frame__authority-facts">
              <div><dt>{labels.authoritySource}</dt><dd>{selectedMode.authority.source}</dd></div>
              <div><dt>{labels.authorityPosture}</dt><dd>{selectedMode.authority.posture}</dd></div>
              <div><dt>{labels.grantor}</dt><dd>{selectedMode.authority.grantor}</dd></div>
              <div><dt>{labels.escalationOwner}</dt><dd>{selectedMode.authority.escalationOwner}</dd></div>
            </dl>
            {selectedMode.authority.grantRequirement ? <p>{selectedMode.authority.grantRequirement}</p> : null}
            <div className="apf-module-agent-supervision-frame__authority-groups">
              <section><h5>{labels.actionScope}</h5><ul>{selectedMode.authority.allowedActionClasses.map((item, index) => <li key={index}>{item}</li>)}</ul></section>
              <section><h5>{labels.interventionRights}</h5><p>{selectedMode.authority.interventionRights}</p></section>
              <section><h5>{labels.humanSupervision}</h5><ul>{selectedMode.authority.humanSupervision.map((item, index) => <li key={index}>{item}</li>)}</ul></section>
              <section><h5>{labels.escalation}</h5><ul>{selectedMode.authority.escalationTriggers.map((item, index) => <li key={index}>{item}</li>)}</ul></section>
              <section><h5>{labels.recovery}</h5><ul>{selectedMode.authority.recovery.map((item, index) => <li key={index}>{item}</li>)}</ul></section>
            </div>
            <p className="apf-module-agent-supervision-frame__owner">
              <span>{labels.outcomeOwner}</span>
              <strong>{role.outcomeOwner}</strong>
            </p>
          </section>
          <section
            className="apf-module-agent-supervision-frame__outcome"
            data-outcome-state={selectedMode.outcomeState}
          >
            <h4>{labels.outcome}</h4>
            <p className="apf-module-agent-supervision-frame__outcome-state">{selectedMode.outcomeStateLabel}</p>
            <div>{selectedMode.outcome}</div>
            {selectedMode.records?.length ? (
              <ul className="apf-module-agent-supervision-frame__records">
                {selectedMode.records.map((record) => (
                  <li key={record.id} data-outcome-state={record.outcomeState}>
                    <div className="apf-module-agent-supervision-frame__record-heading">
                      <strong>{record.label}</strong>
                      <span>{record.stateLabel}</span>
                    </div>
                    <div>{record.detail}</div>
                    <p className="apf-module-agent-supervision-frame__record-owner"><span>{labels.recordOwner}</span><strong>{record.owner}</strong></p>
                    {record.evidenceRecordIds.length ? (
                      <div className="apf-module-agent-supervision-frame__record-evidence">
                        <span>{labels.evidence}</span>
                        <ul>{record.evidenceRecordIds.flatMap((evidenceId) => {
                          const evidence = evidenceById.get(evidenceId);
                          return evidence ? [<li key={evidenceId}><a href={`#${evidenceAnchor(evidenceId)}`}>{evidence.label}</a></li>] : [];
                        })}</ul>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
          {selectedMode.learning ? <section className="apf-module-agent-supervision-frame__learning"><h4>{labels.learning}</h4><dl><div><dt>{labels.learningEvaluation}</dt><dd>{selectedMode.learning.evaluation}</dd></div><div><dt>{labels.learningProposal}</dt><dd>{selectedMode.learning.proposedAdaptation}</dd></div><div><dt>{labels.releaseOwner}</dt><dd>{selectedMode.learning.releaseOwner}</dd></div></dl><h5>{labels.rollback}</h5><ul>{selectedMode.learning.rollback.map((item, index) => <li key={index}>{item}</li>)}</ul></section> : null}
        </div>
        {selectedMode.officeHandoff ? (
          <aside className="apf-module-agent-supervision-frame__office">
            <h4>{labels.officeHandoff}</h4>
            {"steps" in selectedMode.officeHandoff ? <ol className="apf-module-agent-supervision-frame__office-sequence">
              {selectedMode.officeHandoff.steps.map((step) => (
                <li key={`${step.product}-${step.state}`} data-office-state={step.state}>
                  <div className="apf-module-agent-supervision-frame__office-heading">
                    <span>{step.productLabel}</span>
                    <strong>{step.label}</strong>
                    <span>{step.stateLabel}</span>
                  </div>
                  <dl>
                    <div><dt>{labels.artifact}</dt><dd>{step.artifactType}</dd></div>
                    <div><dt>{labels.reviewedVersion}</dt><dd>{step.reviewedVersion}</dd></div>
                    <div><dt>{labels.scope}</dt><dd>{step.scope}</dd></div>
                    <div><dt>{labels.proposedMutation}</dt><dd>{step.proposal}</dd></div>
                    <div><dt>{labels.readBackResult}</dt><dd>{step.readBackResult}</dd></div>
                  </dl>
                  <Link to={step.to}>{step.linkLabel}</Link>
                </li>
              ))}
              <li
                className="apf-module-agent-supervision-frame__office-return"
                data-outcome-state={selectedMode.officeHandoff.returnState}
              >
                <div className="apf-module-agent-supervision-frame__office-heading">
                  <span>{labels.workblockReturn}</span>
                  <strong>{selectedMode.officeHandoff.returnLabel}</strong>
                  <span>{selectedMode.officeHandoff.returnStateLabel}</span>
                </div>
                <div>{selectedMode.officeHandoff.returnRecord}</div>
              </li>
            </ol> : <>
              <p>{selectedMode.officeHandoff.description}</p>
              <Link to={selectedMode.officeHandoff.to}>{selectedMode.officeHandoff.label}</Link>
            </>}
          </aside>
        ) : null}
        {proofClaim ? <p className="apf-module-agent-supervision-frame__claim">{proofClaim}</p> : null}
      </div>
    </section>
  );
}
